import { assertBoolean } from '../validators';
import { TimeInvariantBase } from './TimeInvariantBase';
import { observable } from './reactivity';

/**
 * Capabilities class. "Capabilities" are flags that persist with the video (we load & save)
 * that control what is valid for the video.
 * They typically fall into two categories:
 *   1. Pricing-related capabilities: certain video functionality is only available
 *      to certain pricing plans, so we add flags to control this. `customWatermark` for example.
 *   2. Special video cases: certain use cases require some different validation.
 */
export class Capabilities extends TimeInvariantBase {
    override _classType = 'Capabilities';
    override _name = 'Capabilities';

    #customWatermark = true;
    #enableEnhancedAssetTransform = false;
    @observable accessor #noBranding = true;
    #render1080p = true;
    #render720p = true;

    /**
     * can the user set a watermark
     */
    get customWatermark() {
        return this.#customWatermark;
    }

    set customWatermark(value: boolean) {
        if (this.customWatermark === value) {
            return;
        }
        assertBoolean(value);
        this.#customWatermark = value;
        this.requiresUpdate = true;
    }

    /**
     * can text assets keep their font size instead of auto-shrinking to fit, surfacing overflow
     * through `TextAsset.isClipping`
     */
    get enableEnhancedAssetTransform() {
        return this.#enableEnhancedAssetTransform;
    }

    set enableEnhancedAssetTransform(value: boolean) {
        if (this.enableEnhancedAssetTransform === value) {
            return;
        }
        assertBoolean(value);
        this.#enableEnhancedAssetTransform = value;
        this.requiresUpdate = true;
    }

    /**
     * @internal
     * Sets the value without running the update flow, for `Luminary` to seed its default.
     */
    _seedEnableEnhancedAssetTransform(value: boolean) {
        assertBoolean(value);
        this.#enableEnhancedAssetTransform = value;
    }

    /**
     * can the user render videos without Lumen5 branding
     */
    get noBranding() {
        return this.#noBranding;
    }

    set noBranding(value: boolean) {
        if (this.noBranding === value) {
            return;
        }
        assertBoolean(value);
        this.#noBranding = value;
        this.requiresUpdate = true;
    }

    /**
     * can the user render videos in 1080p (if false, must be 480p or 720p)
     */
    get render1080p() {
        return this.#render1080p;
    }

    set render1080p(value: boolean) {
        if (this.render1080p === value) {
            return;
        }
        assertBoolean(value);
        this.#render1080p = value;
        this.requiresUpdate = true;
    }

    /**
     * can the user render videos in 720p (if false, must be 480p)
     */
    get render720p() {
        return this.#render720p;
    }

    set render720p(value: boolean) {
        if (this.render720p === value) {
            return;
        }
        assertBoolean(value);
        this.#render720p = value;
        this.requiresUpdate = true;
    }

    override clear() {
        this.#customWatermark = true;
        this.#enableEnhancedAssetTransform = false;
        this.#noBranding = true;
        this.#render1080p = true;
        this.#render720p = true;
    }

    override get propertiesToSerialize() {
        return [
            'customWatermark',
            'enableEnhancedAssetTransform',
            'noBranding',
            'render1080p',
            'render720p',
        ];
    }
}
